import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { TooltipProps } from 'recharts';
import Button from '@components/common/Button';
import { buildAssessorLoadingChart } from '@utils/assessorLoadingChart';
import type { AssessorLoadingChartModel } from '@utils/assessorLoadingChart';
import type { AssessorLoadingData } from '@app-types';
import { AssessorFilterDropdown } from './AssessorFilterDropdown';
import styles from './AssessorLoadingChart.module.css';

interface AssessorLoadingChartProps {
  /** `YYYY-MM` shown in the picker and used to lay out the days. */
  month: string;
  onMonthChange: (month: string) => void;
  data: AssessorLoadingData | undefined;
  isLoading: boolean;
  isError?: boolean;
  /** Shown as a "Try again" action on the error message when given. */
  onRetry?: () => void;
}

const CHART_HEIGHT = 320;
// Below these widths the bars of a busy day turn into slivers, so the chart scrolls sideways instead.
const MIN_DAY_WIDTH_PX = 28;
const MIN_BAR_WIDTH_PX = 8;

function chartMinWidth(dayCount: number, assessorCount: number): number {
  return dayCount * Math.max(MIN_DAY_WIDTH_PX, assessorCount * MIN_BAR_WIDTH_PX + 8);
}

interface DayTooltipProps extends TooltipProps<number, string> {
  model: AssessorLoadingChartModel;
}

const DayTooltip: React.FC<DayTooltipProps> = ({ active, payload, model }) => {
  if (!active || !payload?.length) return null;

  const day = (payload[0].payload as { day: string }).day;
  const row = model.rows.find((candidate) => candidate.day === day);

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipDay}>{day}</div>
      {model.assessors.map((assessor) => {
        const total = model.assessorDayTotal(assessor.id, day);
        if (total === 0 || !row) return null;
        return (
          <div key={assessor.id} className={styles.tooltipAssessor}>
            <strong>
              {assessor.name}: {total}
            </strong>
            <ul>
              {model.series
                .filter((series) => series.assessorId === assessor.id && Number(row[series.key]) > 0)
                .map((series) => (
                  <li key={series.key}>
                    {series.tierLabel}: {row[series.key]}
                  </li>
                ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Scheduled Meetings per Assessor per day for one month, each Assessor's stack broken down by Tier.
 * Takes the month as a controlled value so the Scheduler and Program Lead launch pages can share it.
 */
const AssessorLoadingChart: React.FC<AssessorLoadingChartProps> = ({
  month,
  onMonthChange,
  data,
  isLoading,
  isError = false,
  onRetry
}) => {
  // Kept across months: an Assessor without Meetings in the new month simply draws nothing.
  const [selectedAssessorIds, setSelectedAssessorIds] = useState<number[]>([]);
  const model = useMemo(
    () => buildAssessorLoadingChart(month, data?.rows ?? [], selectedAssessorIds),
    [month, data, selectedAssessorIds]
  );

  const renderBody = () => {
    if (isError) {
      return (
        <div className={styles.message}>
          <p>Failed to load Assessor Loading.</p>
          {onRetry && (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Try again
            </Button>
          )}
        </div>
      );
    }
    if (isLoading) {
      return (
        <div
          className={styles.loading}
          data-test-id="assessor-loading-chart-loading"
          style={{ height: CHART_HEIGHT }}
          aria-busy="true"
        />
      );
    }
    if (model.isEmpty) {
      return <p className={styles.message}>No scheduled Meetings in this month</p>;
    }

    return (
      <>
        <div className={styles.toolbar}>
          <AssessorFilterDropdown
            assessors={model.allAssessors}
            selectedAssessorIds={selectedAssessorIds}
            onSelectionChange={setSelectedAssessorIds}
          />
        </div>
        <ul className={styles.legend} data-test-id="assessor-loading-tier-legend" aria-label="Tiers">
          {model.tiers.map((tier) => (
            <li key={tier.key} className={styles.legendItem}>
              <span className={styles.swatch} style={{ backgroundColor: tier.color }} aria-hidden="true" />
              {tier.label}
            </li>
          ))}
        </ul>
        {model.isSelectionEmpty ? (
          <p className={styles.message}>No scheduled Meetings for the selected Assessors in this month</p>
        ) : (
          <>
            <p className={styles.assessorOrder} data-test-id="assessor-loading-assessor-order">
              Bars within each day, left to right: {model.assessors.map((assessor) => assessor.name).join(', ')}
            </p>
            <div className={styles.scroll} data-test-id="assessor-loading-chart-scroll">
              <div style={{ minWidth: chartMinWidth(model.rows.length, model.assessors.length) }}>
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={model.rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" fontSize={12} interval={0} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip content={<DayTooltip model={model} />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
                    {model.series.map((series) => (
                      <Bar
                        key={series.key}
                        dataKey={series.key}
                        stackId={String(series.assessorId)}
                        fill={series.color}
                        isAnimationActive={false}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className={styles.container} data-test-id="assessor-loading-chart">
      <div className={styles.header}>
        <div>
          <h3>Assessor Loading</h3>
          <p>Scheduled Meetings per Assessor per day, by Tier</p>
        </div>
        <label className={styles.monthPicker}>
          <span>Month</span>
          <input
            type="month"
            value={month}
            onChange={(event) => {
              if (event.target.value) onMonthChange(event.target.value);
            }}
          />
        </label>
      </div>
      {renderBody()}
    </div>
  );
};

export { AssessorLoadingChart };
